'use client';

import type { StaticImport } from 'next/dist/shared/lib/get-img-props';
// biome-ignore lint/style/noRestrictedImports: Used to create our custom Image component
import { default as NextImage, type ImageProps as NextImageProps } from 'next/image';
import { AnalyticsEvent } from '@/app_lib/types/analytics';
import { cn } from '@/app_lib/utils/utils';
import { placeholderFortuneImage } from '@/appconstants';

const dataBlur =
  'data:image/jpeg;base64,/9j/4AAQSkZJRgABAQAAAQABAAD/2wBDABQODxIPDRQSEBIXFRQYHjIhHhwcHj0sLiQySUBMS0dARkVQWnNiUFVtVkVGZIhlbXd7gYKBTmCNl4x9lnN+gXz/2wBDARUXFx4aHjshITt8U0ZTfHx8fHx8fHx8fHx8fHx8fHx8fHx8fHx8fHx8fHx8fHx8fHx8fHx8fHx8fHx8fHx8fHz/wAARCAAIAA8DASIAAhEBAxEB/8QAFQABAQAAAAAAAAAAAAAAAAAAAAb/xAAhEAACAQIGAwAAAAAAAAAAAAABAgMABAUGESExQVFhcf/EABUBAQEAAAAAAAAAAAAAAAAAAAAB/8QAFBEBAAAAAAAAAAAAAAAAAAAAAP/aAAwDAQACEQMRAD8AmzHMVs7vSyWcdxKj28bSsqEkKSQPPFFFAikn5P/Z';

/**
 * Transforms an image source URL to request the WebP format.
 *
 * If the source URL already includes the 'format=webp' query parameter or is undefined,
 * the function returns the original source. Otherwise, it appends 'format=webp' as a query
 * parameter, using '&' or '?' as appropriate.
 *
 * @param src - The image source URL or StaticImport to transform.
 * @returns The transformed image source URL with 'format=webp' appended, or the original source if already in WebP format or undefined.
 */
function transformToWebp(src?: string | StaticImport) {
  if (src?.toString().includes('format=webp') || !src) {
    return src;
  }
  return `${src}${src.toString().includes('?') ? '&' : '?'}format=webp`;
}

const handleBrokenImage = (event: React.SyntheticEvent<HTMLImageElement, Event>) => {
  try {
    const target = event.currentTarget;
    if (target.src !== placeholderFortuneImage) {
      target.src = placeholderFortuneImage; // Set the placeholder image
      target.srcset = ''; // Clear srcset to ensure only the placeholder is used
    }
  } catch (_error) {
    // console.error('Error handling broken image:', error);
  }
};

export interface ImageProps extends Omit<NextImageProps, 'placeholder'> {
  sources?: {
    srcSet: string;
    type: string;
  }[];
  quality?: number;
  blurDataUrl?: string;
  className?: string;
  pictureClassName?: string;
  fullWidth?: boolean;
  fillProp?: boolean;
}

// TODO: Consider setting the default loading behavior to 'lazy' for performance optimization
// TODO: Make sure this component was made correctly
export const Image = ({
  src,
  alt,
  width = 300,
  height = 200,
  blurDataUrl = dataBlur,
  className,
  pictureClassName,
  quality = 75,
  fullWidth = true,
  loading = 'lazy',
  priority = false,
  fillProp = false,
  ...props
}: ImageProps) => {
  const defaultQuality = 75;

  const webpSrc = transformToWebp(src);

  return (
    <picture className={cn(`block ${fullWidth ? 'w-full' : ''}`, pictureClassName)}>
      <NextImage
        alt={alt}
        blurDataURL={blurDataUrl}
        className={cn('transition-opacity duration-300', className, AnalyticsEvent.ImpressionElement)}
        data-cy={props['data-cy'] || 'image-component'}
        {...(!fillProp && { height })}
        loading={priority ? 'eager' : loading}
        onError={handleBrokenImage}
        placeholder='blur'
        priority={priority}
        quality={quality || defaultQuality}
        sizes={
          fullWidth
            ? '(max-width: 320px) 50vw, (max-width: 768px) 85vw, (max-width: 1024px) 50vw, (max-width: 1200px) 40vw, 33vw'
            : ''
        }
        src={webpSrc || placeholderFortuneImage}
        {...(!fillProp && { width })}
        {...props}
      />
    </picture>
  );
};
